import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { observer } from "mobx-react-lite";
import {
  X,
  Search,
  Star,
  Check,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTran } from "@/hooks/useTran";
import { useQuestionLibraryStore } from "../hooks";
import QuestionSearchFilter from "./QuestionSearchFilter";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

const TypeBadge = ({ type }) => (
  <span className="rounded-full bg-primary/10 px-2 py-1 text-[11px] font-semibold capitalize text-primary">
    {type?.replace(/_/g, " ")}
  </span>
);

const QuestionCard = ({ question, isSelected, isAlready, onToggle }) => (
  <button
    type="button"
    onClick={() => !isAlready && onToggle(question)}
    disabled={isAlready}
    className={cn(
      "relative w-full text-left border rounded-xl p-4 transition-all",
      isSelected
        ? "border-emerald-400 bg-emerald-50 shadow-sm dark:border-emerald-500 dark:bg-emerald-950/40"
        : isAlready
          ? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed dark:border-gray-700 dark:bg-gray-800/50"
          : "border-gray-200 bg-white hover:border-indigo-300 hover:shadow-sm cursor-pointer dark:border-gray-700 dark:bg-gray-800 dark:hover:border-indigo-500",
    )}
  >
    {isSelected && (
      <span className="absolute top-3 right-3 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
        <Check className="w-3 h-3 text-white" />
      </span>
    )}
    {isAlready && !isSelected && (
      <span className="absolute top-3 right-3 text-xs text-gray-400 dark:text-gray-500 italic">
        Added
      </span>
    )}
    <div className="flex items-center justify-between mb-2">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
        {question.category || "—"}
      </span>
      <Star
        className={cn(
          "w-4 h-4",
          question.isFavourite
            ? "fill-yellow-400 text-yellow-400"
            : "text-gray-300 dark:text-gray-600",
        )}
      />
    </div>
    <p className="text-sm font-semibold mb-3 line-clamp-2 text-gray-900 dark:text-gray-100">
      {question.questionText}
    </p>
    <div className="flex items-center justify-between">
      <TypeBadge type={question.questionType} />
      <span className="text-xs text-gray-400 dark:text-gray-500">
        Used in {question.usedIn?.length ?? 0} surveys
      </span>
    </div>
  </button>
);

const QuestionLibraryDialog = observer(
  ({ isOpen, onClose, onConfirm, alreadySelected = [] }) => {
    const { t } = useTran();
    const store = useQuestionLibraryStore();

    // ✅ ordered array — preserves click order
    const [selected, setSelected] = useState([]);

    useEffect(() => {
      if (!isOpen) return;
      setSelected([]);
      store.resetFilters();
      store.fetchQuestions();
    }, [isOpen]);

    useEffect(() => {
      if (!isOpen) return;
      const delay = store.filters.search ? 300 : 0;
      const id = setTimeout(() => store.fetchQuestions(), delay);
      return () => clearTimeout(id);
    }, [
      store.pagination.page,
      store.filters.search,
      store.filters.category,
      store.filters.type,
      store.filters.sort,
      store.filters.showFavourites,
      store.filters.showArchived,
      isOpen,
    ]);

    const { start, end, total } = store.paginationInfo;

    // ✅ append on click → preserves order
    const toggleQuestion = useCallback((question) => {
      setSelected((prev) => {
        const exists = prev.some((q) => q._id === question._id);
        return exists
          ? prev.filter((q) => q._id !== question._id)
          : [...prev, question];
      });
    }, []);

    // ✅ selected is already in click order
    const handleConfirm = () => {
      const picked = selected.filter((q) => !alreadySelected.includes(q._id));
      onConfirm(picked);
      store.resetFilters();
      setSelected([]);
    };

    const handleClose = () => {
      store.resetFilters();
      setSelected([]);
      onClose();
    };

    const newlyAdded = selected.length; // ✅ .length not .size

    if (!isOpen) return null;

    return createPortal(
      <>
        <div
          className="fixed inset-0 z-50 bg-black/40 dark:bg-black/60"
          onClick={handleClose}
        />
        <div
          className={cn(
            "fixed inset-4 md:inset-8 z-50 flex flex-col overflow-hidden",
            "rounded-2xl shadow-2xl bg-white dark:bg-gray-900",
            "border border-transparent dark:border-gray-700",
          )}
        >
          {/* Header */}
          <div
            className={cn(
              "flex items-center justify-between px-6 py-4 border-b shrink-0",
              "border-gray-200 dark:border-gray-700",
            )}
          >
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {t("Question Library")}
              </h2>
              <p className="text-sm mt-0.5 text-gray-500 dark:text-gray-400">
                {t("Select questions to add to your survey.")}
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="p-2 rounded-lg transition-colors hover:bg-gray-100 text-gray-400 dark:hover:bg-gray-800 dark:text-gray-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Filters */}
          <div
            className={cn(
              "px-6 py-3 border-b shrink-0",
              "border-gray-200 dark:border-gray-700",
            )}
          >
            <QuestionSearchFilter
              filters={store.filters}
              onFilterChange={(key, value) => store.setFilter(key, value)}
            />
          </div>

          {/* Results + Pagination */}
          {!store.loading && (
            <div
              className={cn(
                "shrink-0 flex items-center justify-between px-6 py-2 border-b",
                "border-gray-200 dark:border-gray-700",
              )}
            >
              <span className="text-sm text-muted-foreground dark:text-gray-400">
                {total > 0
                  ? `Showing ${start}–${end} of ${total}`
                  : "No questions found"}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={!store.canGoPrev}
                  onClick={() => store.goPrevPage()}
                  className="gap-1"
                >
                  <ChevronLeft className="h-4 w-4" /> Previous
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={!store.canGoNext}
                  onClick={() => store.goNextPage()}
                  className="gap-1"
                >
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Question Grid */}
          <div className="flex-1 overflow-y-auto p-6">
            {store.loading ? (
              <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-36 rounded-xl animate-pulse bg-gray-100 dark:bg-gray-800"
                  />
                ))}
              </div>
            ) : store.questions.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 py-20">
                <Search className="w-10 h-10 text-gray-300 dark:text-gray-600" />
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  {t("No questions found.")}
                </p>
              </div>
            ) : (
              <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                {store.questions.map((q) => (
                  <QuestionCard
                    key={q._id}
                    question={q}
                    isSelected={selected.some((s) => s._id === q._id)} // ✅
                    isAlready={alreadySelected.includes(q._id)}
                    onToggle={toggleQuestion}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            className={cn(
              "shrink-0 border-t px-6 py-4 flex items-center justify-between",
              "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700",
            )}
          >
            <span className="text-sm text-gray-400 dark:text-gray-500">
              {newlyAdded > 0
                ? `${newlyAdded} ${t("question(s) selected")}`
                : total > 0
                  ? `${total} ${t("questions available")}`
                  : ""}
            </span>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleClose}>
                {t("Cancel")}
              </Button>
              <Button
                type="button"
                disabled={newlyAdded === 0}
                onClick={handleConfirm}
              >
                {t("Add to Survey")}
                {newlyAdded > 0 && (
                  <Badge className="bg-card text-foreground ring-1 ring-border">
                    {newlyAdded}
                  </Badge>
                )}
              </Button>
            </div>
          </div>
        </div>
      </>,
      document.body,
    );
  },
);

export default QuestionLibraryDialog;
